import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { clearSupabaseStorage, isDeleting } from "@/lib/authCleanup";
import { writeAttributionIfNew } from "@/lib/utmAttribution";
import { clearSessionTracking } from "@/lib/analytics";
import { isEntitlementActive } from "@/lib/entitlementActive";
import {
  isNotificationsAvailable,
  isAlreadySubscribed,
  identifyOneSignalUser,
  cancelLegacyLocalSchedules,
} from "@/lib/notifications";
import type { User, Session } from "@supabase/supabase-js";
import { useEntitlementOverride } from "@/lib/entitlementOverrideStore";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  adminLoaded: boolean;
  isKinInboxTester: boolean;
  isPro: boolean;
  proLoaded: boolean;
  adminPreview: boolean;
  signOut: () => Promise<void>;
  showReviewPrompt: boolean;
  dismissReviewPrompt: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  adminLoaded: false,
  isKinInboxTester: false,
  isPro: false,
  proLoaded: false,
  adminPreview: false,
  signOut: async () => {},
  showReviewPrompt: false,
  dismissReviewPrompt: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoaded, setAdminLoaded] = useState(false);
  const [isKinInboxTester, setIsKinInboxTester] = useState(false);
  const [isPro, setIsPro] = useState(false);

  /**
   * #632 — the entitlements read below runs ONCE per sign-in (deps
   * [user?.id, loading]), so `isPro` alone is frozen for the life of the
   * session. A mom who upgrades mid-session kept the free experience in
   * anything reading it directly.
   *
   * That produced the launch-day bug: she bought Pro, the webhook wrote the
   * row, /pro correctly said "You're already Pro", and the Tasks tab still
   * showed her the free-tier promo and locked every scheduled task — same
   * session. #631 fixed those two call sites; this fixes the value.
   *
   * TasksTab.tsx and Settings.tsx both reached for the raw value
   * independently, by different authors, months apart. That is a trap in the
   * shape of the API, not two careless mistakes: `useAuth().isPro` and
   * `useIsPro()` looked equally reasonable, and the broken one was shorter.
   *
   * Folding the override in HERE means the two can no longer disagree, so
   * reading the raw value is no longer a bug. The guard test from #631 stays
   * as belt-and-braces, not as the only thing standing between a paying
   * customer and the free experience.
   */
  const entitlementOverride = useEntitlementOverride();
  const [proLoaded, setProLoaded] = useState(false);
  const [adminPreview, setAdminPreview] = useState(false);

  // is_admin + inbox_tester in one query — both are per-user elevated-access
  // flags on profiles (see supabase/migrations/20260720090000_kin_inbox_tester_allowlist.sql).
  // `as any` cast: inbox_tester isn't in the generated types.ts yet (stale
  // until Kasandra runs the migration + regenerates), same workaround as
  // FamilyProfileSection.tsx's family_context reads.
  useEffect(() => {
    if (!user?.id) {
      setIsAdmin(false);
      setIsKinInboxTester(false);
      setAdminLoaded(!loading);
      return;
    }
    let cancelled = false;
    setAdminLoaded(false);
    (async () => {
      try {
        const { data } = await (supabase as any)
          .from("profiles")
          .select("is_admin, inbox_tester")
          .eq("id", user.id)
          .maybeSingle();
        if (cancelled) return;
        setIsAdmin(data?.is_admin === true);
        setIsKinInboxTester(data?.inbox_tester === true);
        setAdminLoaded(true);
      } catch {
        if (cancelled) return;
        setIsAdmin(false);
        setIsKinInboxTester(false);
        setAdminLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, loading]);

  // Pro entitlement + admin preview flag. Reads the user's row in the
  // entitlements table. Pro = tier 'pro' and not expired. adminPreview is the
  // builder god-mode flag, only meaningful when the user is also is_admin
  // (enforced in features.ts). Fails CLOSED on any error (never unlocks Pro or
  // preview by accident). A free user simply has no row (.maybeSingle -> null).
  useEffect(() => {
    if (!user?.id) {
      setIsPro(false);
      setAdminPreview(false);
      setProLoaded(!loading);
      return;
    }
    let cancelled = false;
    setProLoaded(false);
    (async () => {
      try {
        const { data } = await supabase
          .from("entitlements")
          .select("tier, expires_at, admin_preview")
          .eq("user_id", user.id)
          .maybeSingle();
        if (cancelled) return;
        setIsPro(isEntitlementActive(data));
        setAdminPreview(data?.admin_preview === true);
        setProLoaded(true);
      } catch {
        if (cancelled) return;
        setIsPro(false);
        setAdminPreview(false);
        setProLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, loading]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      // On sign-in, flush any captured UTM attribution. The helper itself
      // skips the write if a row already exists (so logins are no-ops).
      // created_at is passed through because SIGNED_IN also fires for ordinary
      // logins — the helper uses account age to tell a signup from a login
      // (#351), otherwise a returning user's login would write a row dated
      // today and read as a brand new signup in the daily digest.
      if (event === "SIGNED_IN" && session?.user?.id) {
        const userId = session.user.id;
        const createdAt = session.user.created_at;
        setTimeout(() => { void writeAttributionIfNew(userId, createdAt); }, 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      // Zombie session safety net: a session token exists in storage but the
      // server rejected it (user no longer in auth.users). Wipe and bounce.
      if (error && !isDeleting()) {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) {
          console.warn("Zombie session detected, clearing local auth state.");
          supabase.auth.signOut({ scope: "local" }).catch(() => {});
          clearSupabaseStorage();
          if (typeof window !== "undefined" && window.location.pathname !== "/") {
            window.location.href = "/";
            return;
          }
        }
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Re-identify the OneSignal subscription with this user's id whenever an
  // authenticated session becomes available. Covers the returning-user case:
  // the user accepted the soft-prompt in a prior session and is now signing
  // in on a fresh install / new device, so register() has already happened
  // and isAlreadySubscribed() is true at mount. Idempotent and best-effort.
  //
  // Also runs the #836 upgrade cleanup here (cancelLegacyLocalSchedules) —
  // a no-op after the first call on a given device, so piggybacking it on
  // this already-existing, already-idempotent, once-per-session effect
  // needs no gating flag of its own.
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        if (!isNotificationsAvailable()) return;
        void cancelLegacyLocalSchedules();
        const subscribed = await isAlreadySubscribed();
        if (cancelled || !subscribed) return;
        identifyOneSignalUser(userId);
      } catch (err) {
        console.warn("[OneSignalIdentify] mount-time identify failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Increment login_count in user_metadata once per browser session
  useEffect(() => {
    if (!user?.id) return;
    if (sessionStorage.getItem("kin_login_counted")) return;
    sessionStorage.setItem("kin_login_counted", "true");

    (async () => {
      try {
        const { data: { user: freshUser } } = await supabase.auth.getUser();
        const meta = (freshUser?.user_metadata || {}) as { login_count?: number };
        const currentCount = meta.login_count || 0;
        await supabase.auth.updateUser({ data: { login_count: currentCount + 1 } });
      } catch (err) {
        console.error("Login count update failed (non-fatal):", err);
      }
    })();
  }, [user?.id]);

  // Trigger App Store review prompt on 3rd+ login (one-time)
  useEffect(() => {
    if (!user?.id) return;
    const timer = setTimeout(async () => {
      try {
        const { data: { user: freshUser } } = await supabase.auth.getUser();
        const meta = (freshUser?.user_metadata || {}) as {
          login_count?: number;
          review_prompted?: boolean;
        };
        const loginCount = meta.login_count || 0;
        const reviewPrompted = meta.review_prompted || false;
        if (loginCount >= 3 && !reviewPrompted) {
          setShowReviewPrompt(true);
        }
      } catch {
        // Non-fatal
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [user?.id]);

  const dismissReviewPrompt = () => setShowReviewPrompt(false);

  const signOut = async () => {
    clearSessionTracking();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, isAdmin, adminLoaded, isKinInboxTester, isPro: isPro || entitlementOverride === true, proLoaded, adminPreview, signOut, showReviewPrompt, dismissReviewPrompt }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
